import { LoaderIcon } from '@/app/features/icons/Loader'
import { faClose, FaIcon } from '@/lib/fa'
import AddCountryCodeForm from '@/modules/drivers/forms/activities/AddCountryCodeForm'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import { PlaceEntryType } from '@/app/constants/placeEntryType'
import moment from 'moment'
import React from 'react'

const AddCountryCodeModal = ({
  handleClose,
  companyId,
  driverId,
  defaultDateTime,
}: {
  handleClose: () => void
  companyId: string
  driverId: string

  defaultDateTime?: string | null
}) => {
  const { t, tSafe } = useLocalize()
  const [loading, setLoading] = React.useState(false)
  const initialDateTime = defaultDateTime ?? moment().format('YYYY-MM-DD HH:mm')
  const [data, setData] = React.useState<any>({
    startDateTime: initialDateTime,
    entryType: PlaceEntryType.BEGIN,
    nationality: null,
  })
  const { addPlace } = useDriverActivityService()
  const { triggerRefresh } = useActivity()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    const dateTime = moment(data.startDateTime, 'YYYY-MM-DD HH:mm', true)
    if (!data.nationality?.nationId || !dateTime.isValid()) {
      toast.error({ text: t('countryCode.invalid-entry') })
      return
    }
    const startTime = dateTime.toISOString()
    const place = {
      nationCode: data.nationality.nationId,
      entryType: data.entryType,
      startTime,
      endTime: startTime,
      odometerValue: 0,
    }

    setLoading(true)
    try {
      await addPlace({
        companyId,
        driverId: Number(driverId),
        place,
      })
      toast.info({
        title: tSafe('countryCode.add-success', 'Wpis dodany'),
        text: tSafe(
          'countryCode.add-success-details',
          'Kod kraju został dodany.',
        ),
      })

      triggerRefresh()
      handleClose()
    } catch (err: any) {
      const code = err?.response?.data?.code ?? err?.code ?? null
      toast.error({
        text: tSafe(
          `errorCode.${code ?? 'GENERIC'}`,
          tSafe('countryCode.add-error', 'Nie udało się dodać wpisu.'),
        ),
      })
      console.error('Add place failed', err)
    } finally {
      setLoading(false)
    }
  }
  return (
    <ModalLayout customZIndex={9999}>
      <form
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="countryCode-title"
        className="tw-relative tw-m-4 tw-flex tw-h-fit tw-max-h-[90vh] tw-w-full tw-max-w-[480px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !loading) {
            e.stopPropagation()
            handleClose()
          }
          if (e.key === 'Tab') {
            const controls = e.currentTarget.querySelectorAll<HTMLElement>(
              'button:not(:disabled), input:not(:disabled), select:not(:disabled)',
            )
            const first = controls[0]
            const last = controls[controls.length - 1]
            if (
              !first ||
              (e.shiftKey ? e.target === first : e.target === last)
            ) {
              e.preventDefault()
              ;(e.shiftKey ? last : first)?.focus()
            }
          }
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        {loading && (
          <div className="tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-place-items-center tw-rounded-lg tw-bg-black/30 tw-backdrop-blur-sm">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p
              id="countryCode-title"
              className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base"
            >
              {t('countryCode.add-entry')}
            </p>
          </div>
          <button
            type="button"
            autoFocus
            disabled={loading}
            aria-label={t('operation.close')}
            onClick={handleClose}
          >
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-text-content-base hover:tw-text-primary"
            />
          </button>
        </div>
        <fieldset disabled={loading} className="tw-min-h-0 tw-overflow-y-auto">
          <AddCountryCodeForm
            data={data}
            setData={setData}
            companyId={companyId}
          />
        </fieldset>
        <div className="tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-base-300 tw-p-4">
          <button
            type="button"
            disabled={loading}
            onClick={handleClose}
            className="tw-rounded-lg tw-p-3 tw-text-sm"
          >
            {t('operation.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            title={t('operation.confirm')}
            className="tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-80 disabled:tw-opacity-50"
          >
            {t('operation.confirm')}
          </button>
        </div>
      </form>
    </ModalLayout>
  )
}

export default AddCountryCodeModal
